import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/esm/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/esm/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import { error as _error } from "@instructure/console";

var _dec, _dec2, _dec3, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component, createElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import keycode from 'keycode';
import { View } from '@instructure/ui-view';
import { Focusable } from '@instructure/ui-focusable';
import { themeable, ThemeablePropTypes } from '@instructure/ui-themeable';
import { Children, controllable } from '@instructure/ui-prop-types';
import { deprecated, matchComponentTypes, safeCloneElement, passthroughProps } from '@instructure/ui-react-utils';
import { uid } from '@instructure/uid';
import { testable } from '@instructure/ui-testable';
import { Tab } from './Tab';
import { TabPanel } from './TabPanel';
var styles = {
  componentId: 'cMXVf',
  template: function template(theme) {
    return "\n\n.cMXVf_bXVH{display:flex;flex-flow:row wrap}\n\n.cMXVf_GKUc{background:".concat(theme.minimalBackground || 'inherit', "}");
  },
  'tabs': 'cMXVf_bXVH',
  'minimal': 'cMXVf_GKUc'
};
import theme from './theme';
/**
---
category: components/deprecated
id: DeprecatedTabList
---
**/

var TabList = (_dec = deprecated('7.0.0', null, 'Use Tabs from ui-tabs instead.'), _dec2 = testable(), _dec3 = themeable(theme, styles), _dec(_class = _dec2(_class = _dec3(_class = (_temp = _class2 =
/*#__PURE__*/
function (_Component) {
  _inherits(TabList, _Component);

  function TabList(props) {
    var _this;

    _classCallCheck(this, TabList);

    _this = _possibleConstructorReturn(this, _getPrototypeOf(TabList).call(this));

    _this.handleClick = function (tabIndex, e) {
      var tab = _this.getTab(tabIndex);

      if (!tab.props.disabled) {
        _this.setSelected(tabIndex);
      }
    };

    _this.handleKeyDown = function (tabIndex, e) {
      var index = _this.selectedIndex;
      var preventDefault = false;

      if (e.keyCode === keycode.codes.up || e.keyCode === keycode.codes.left) {
        // Select next tab to the left
        index = _this.getIndex(index, -1);
        preventDefault = true;
      } else if (e.keyCode === keycode.codes.down || e.keyCode === keycode.codes.right) {
        // Select next tab to the right
        index = _this.getIndex(index, 1);
        preventDefault = true;
      }

      if (preventDefault) {
        e.preventDefault();
      }

      _this.setSelected(index);
    };

    _this.handleFocusableRef = function (el) {
      _this._focusable = el;
    };

    _this.state = {};

    if (typeof props.selectedIndex === 'undefined') {
      _this.state.selectedIndex = props.defaultSelectedIndex;
    }

    _this._tabs = [];
    _this._panels = [];
    return _this;
  }

  _createClass(TabList, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      if (this.props.focus) {
        this.focus();
      }
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (this.props.focus && !prevProps.focus) {
        this.focus();
      }
    }
  }, {
    key: "setSelected",
    value: function setSelected(index) {
      var _this2 = this;

      var selectedIndex; // Check index boundary

      /*#__PURE__*/

      /*#__PURE__*/
      _error(this.isValidIndex(index), "[TabList] Invalid tab index: '".concat(index, "'."));

      var handleChange = function handleChange() {
        if (typeof selectedIndex !== 'undefined' && typeof _this2.props.onChange === 'function') {
          _this2.props.onChange(index, selectedIndex);
        }
      };

      if (typeof this.props.selectedIndex === 'undefined') {
        this.setState(function (state, props) {
          selectedIndex = state.selectedIndex;

          if (index !== selectedIndex) {
            handleChange();
            return {
              selectedIndex: index
            };
          } else {
            return state;
          }
        });
      } else {
        selectedIndex = this.props.selectedIndex;

        if (index !== selectedIndex) {
          handleChange();
        }
      }
    }
  }, {
    key: "getIndex",
    value: function getIndex(startIndex, step) {
      var count = this.tabs.length;
      var change = step < 0 ? step + count : step;

      /*#__PURE__*/

      /*#__PURE__*/
      _error(this.isValidIndex(startIndex), "[Tablist] Invalid tab index: '".concat(startIndex, "'"));

      var index = startIndex;

      do {
        index = (index + change) % count;
      } while (this.getTab(index).props.disabled);

      return index;
    }
  }, {
    key: "isValidIndex",
    value: function isValidIndex(index) {
      return index >= 0 && index < this.tabs.length;
    }
  }, {
    key: "getTab",
    value: function getTab(index) {
      return this._tabs[index];
    }
  }, {
    key: "createTab",
    value: function createTab(index, cachedId, selected, props) {
      var _this3 = this;

      var id = props.id || cachedId;
      return createElement(Tab, {
        variant: this.props.variant,
        ref: function ref(c) {
          _this3._tabs[index] = c;

          if (typeof props.tabRef === 'function') {
            props.tabRef(c);
          }
        },
        key: "tab-".concat(id),
        id: "tab-".concat(id),
        controls: "panel-".concat(id),
        index: index,
        selected: selected,
        disabled: props.disabled,
        children: props.title,
        onClick: this.handleClick,
        onKeyDown: this.handleKeyDown
      });
    }
  }, {
    key: "clonePanel",
    value: function clonePanel(index, cachedId, selected, panel) {
      var _this4 = this;

      var id = panel.props.id || cachedId;
      return safeCloneElement(panel, {
        ref: function ref(c) {
          _this4._panels[index] = c;
        },
        id: "panel-".concat(id),
        labelledBy: "tab-".concat(id),
        selected: selected,
        key: "panel-".concat(id),
        variant: this.props.variant,
        padding: panel.props.padding || this.props.padding,
        textAlign: panel.props.textAlign || this.props.textAlign,
        maxHeight: panel.maxHeight || this.props.maxHeight,
        minHeight: panel.minHeight || this.props.minHeight
      });
    }
  }, {
    key: "focus",
    value: function focus() {
      this._focusable && typeof this._focusable.focus === 'function' && this._focusable.focus();
    }
  }, {
    key: "render",
    value: function render() {
      var _this5 = this;

      var panels = [];
      var tabs = [];
      var ids = this.tabIds;

      var _this$props = this.props,
          children = _this$props.children,
          size = _this$props.size,
          maxWidth = _this$props.maxWidth,
          elementRef = _this$props.elementRef,
          variant = _this$props.variant,
          margin = _this$props.margin,
          onChange = _this$props.onChange,
          props = _objectWithoutProperties(_this$props, ["children", "size", "maxWidth", "elementRef", "variant", "margin", "onChange"]);

      var selectedIndex = React.Children.toArray(children).filter(function (child) {
        return matchComponentTypes(child, [TabPanel]);
      }).findIndex(function (child, index) {
        return !child.props.disabled && index === _this5.selectedIndex;
      });
      var index = 0;
      selectedIndex = selectedIndex >= 0 ? selectedIndex : 0;
      React.Children.forEach(children, function (child) {
        if (matchComponentTypes(child, [TabPanel])) {
          var selected = !child.props.disabled && selectedIndex === index;
          var id = ids[index];
          tabs.push(_this5.createTab(index, id, selected, child.props));
          panels.push(_this5.clonePanel(index, id, selected, child));
          index++;
        } else {
          panels.push(child);
        }
      });
      return React.createElement(View, Object.assign({}, passthroughProps(props), {
        elementRef: elementRef,
        maxWidth: maxWidth ? maxWidth : this.theme[size],
        margin: margin,
        as: "div",
        className: classnames(_defineProperty({}, styles[variant], true))
      }), React.createElement(Focusable, {
        ref: this.handleFocusableRef
      }, function (_ref) {
        var focusVisible = _ref.focusVisible;
        return React.createElement(View, {
          as: "div",
          display: "flex",
          position: "relative",
          borderRadius: "medium",
          withFocusOutline: focusVisible,
          shouldAnimateFocus: false,
          role: "tablist",
          className: styles.tabs
        }, tabs);
      }), panels);
    }
  }, {
    key: "selectedIndex",
    get: function get() {
      return typeof this.props.selectedIndex === 'undefined' ? this.state.selectedIndex : this.props.selectedIndex;
    }
  }, {
    key: "tabIds",
    get: function get() {
      // cache tab ids for better performance and to prevent errors with animations
      var ids = this._tabIds || [];
      var diff = ids.length - this.tabs.length;

      while (diff++ < 0) {
        ids.push(uid('Tab'));
      }

      this._tabIds = ids;
      return ids;
    }
  }, {
    key: "tabs",
    get: function get() {
      return React.Children.toArray(this.props.children).map(function (child) {
        return matchComponentTypes(child, [TabPanel]) && child;
      });
    }
  }]);

  TabList.displayName = "TabList";
  return TabList;
}(Component), _class2.propTypes = {
  /**
  * children of type `TabList.Panel`
  */
  children: Children.oneOf([TabPanel, null]),
  variant: PropTypes.oneOf(['simple', 'minimal']),

  /**
  * the index (zero based) of the panel that should be selected on initial render
  */
  defaultSelectedIndex: PropTypes.number,

  /**
  * the index (zero based) of the panel that should be selected (should be accompanied by `onChange`)
  */
  selectedIndex: controllable(PropTypes.number, 'onChange', 'defaultSelectedIndex'),

  /**
  * Call this function when the selected tab changes. When used with `selectedIndex`,
  * the component will not control its own state.
  */
  onChange: PropTypes.func,

  /**
  * the selected tab should be focused
  */
  focus: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /**
  * Valid values are `0`, `none`, `auto`, `xxx-small`, `xx-small`, `x-small`,
  * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
  * familiar CSS-like shorthand. For example: `margin="small auto large"`.
  */
  margin: ThemeablePropTypes.spacing,

  /**
  * Valid values are `0`, `none`, `xxx-small`, `xx-small`, `x-small`,
  * `small`, `medium`, `large`, `x-large`, `xx-large`. Apply these values via
  * familiar CSS-like shorthand. For example: `padding="small x-large large"`.
  */
  padding: ThemeablePropTypes.spacing,
  textAlign: PropTypes.oneOf(['start', 'center', 'end']),
  elementRef: PropTypes.func
}, _class2.defaultProps = {
  variant: 'simple',
  focus: false,
  defaultSelectedIndex: 0,
  padding: void 0,
  textAlign: void 0,
  size: void 0,
  maxWidth: void 0,
  maxHeight: void 0,
  minHeight: void 0,
  selectedIndex: void 0,
  onChange: void 0,
  margin: void 0,
  children: null,
  elementRef: function elementRef(el) {}
}, _class2.Panel = TabPanel, _class2.Tab = Tab, _temp)) || _class) || _class) || _class);
export default TabList;
export { TabList, TabPanel };